import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PersonOutlineOutlined } from "@mui/icons-material";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../redux/reducers/detailSidebarReducer";
import LoanHeaderChipList, { T } from "./LoanHeaderChipList";

/**
 * Guarantors in the loan drilldown header — same chips + overflow popover as
 * the properties strip (see LoanHeaderChipList), so a loan with many guarantors
 * never grows the header. Clicking a chip or a popover row opens the borrower
 * detail sidebar, the same one the Applicants tab opens.
 *
 * Guarantors are the borrowers flagged `isGuarantor` — on an entity loan the
 * individuals guaranteeing the deal, which is what the header should show.
 */

const guarantorName = (borrower) => {
  const name = `${borrower?.firstName || ""} ${borrower?.lastName || ""}`.trim();
  return name || "Unknown";
};

const LoanHeaderGuarantors = () => {
  const dispatch = useDispatch();
  const borrowers = useSelector((state) => state.loanDrilldown?.borrowers);

  const guarantors = useMemo(
    () => (borrowers || []).filter((b) => b?.isGuarantor),
    [borrowers],
  );

  const handleSelect = useCallback(
    (borrower) => {
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER,
          borrower,
          borrower?._id,
          {
            title: guarantorName(borrower),
            mode: "view",
            source: "LoanHeaderGuarantors",
            sourceField: "borrowers",
          },
        ),
      );
    },
    [dispatch],
  );

  if (guarantors.length === 0) return null;

  return (
    <LoanHeaderChipList
      items={guarantors}
      getLabel={(count) => (count > 1 ? `Guarantors (${count})` : "Guarantor")}
      getChipLabel={guarantorName}
      getTooltip={(b) => b?.emailAddress || guarantorName(b)}
      getPrimary={guarantorName}
      getSecondary={(b) => b?.emailAddress || b?.phoneNumber}
      getSearchTerms={(b) => [
        guarantorName(b),
        b?.emailAddress,
        b?.phoneNumber,
      ]}
      chipIcon={
        <PersonOutlineOutlined sx={{ fontSize: 13, color: T.ink3 }} />
      }
      onSelect={handleSelect}
      filterPlaceholder="Filter by name or email"
      viewAllPath="applicants"
      viewAllLabel="View all in Applicants tab"
    />
  );
};

export default LoanHeaderGuarantors;
